import { parseAbi, formatUnits } from 'viem';

// Contract addresses - PRODUCTION ADDRESSES FROM LATEST DEPLOYMENT V3
export const CONTRACTS = {
  // Core Contracts
  VAULT_CORE: '0xE83522f0882493844C48ADd97Ef03281040e3d2D',
  VAULT_MANAGER: '0x379b83a89314cb9a6abE1b36bFE4B28c2254fd19',

  // Token
  USDC: '0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E',

  // Adapters (25% allocation each)
  AAVE_ADAPTER: '0x801aDAdB10d0Bd70B071eFDc84280d1e8143205F',
  EULER_ADAPTER: '0x41827573A29A795Ee4D3E8AB63C8Bcb2ac50eb1A',
  FORTY_ACRES_ADAPTER: '0x540cF47EaD7A668e4520CB0fA6Ca47eFf472ae93',
  SILO_ADAPTER: '0x6fdac781b90a51087b849775FA2b2acd0d01A0a4',

  // Libraries (for reference)
  ALLOCATION_MANAGER: '0x07f58E8dcb1BFD79e82d007879F10Ff7960cD688',
  USER_ACCOUNTING_LIB: '0x794011B22ee43A6A8a54812cE419530b4b169971',
  EMERGENCY_MANAGER: '0x9BA4453BEf50a9234a61616AA6a9f94516Fa4830',
};

// VaultCore ABI - Main vault functions with new getUserStats
export const VAULT_CORE_ABI = parseAbi([
  // ERC4626 Standard
  'function asset() view returns (address)',
  'function totalAssets() view returns (uint256)',
  'function convertToShares(uint256 assets) view returns (uint256)',
  'function convertToAssets(uint256 shares) view returns (uint256)',
  'function maxDeposit(address) view returns (uint256)',
  'function previewDeposit(uint256 assets) view returns (uint256)',
  'function deposit(uint256 assets, address receiver) returns (uint256)',
  'function maxMint(address) view returns (uint256)',
  'function previewMint(uint256 shares) view returns (uint256)',
  'function mint(uint256 shares, address receiver) returns (uint256)',
  'function maxWithdraw(address owner) view returns (uint256)',
  'function previewWithdraw(uint256 assets) view returns (uint256)',
  'function withdraw(uint256 assets, address receiver, address owner) returns (uint256)',
  'function maxRedeem(address owner) view returns (uint256)',
  'function previewRedeem(uint256 shares) view returns (uint256)',
  'function redeem(uint256 shares, address receiver, address owner) returns (uint256)',

  // ERC20
  'function totalSupply() view returns (uint256)',
  'function balanceOf(address account) view returns (uint256)',
  'function transfer(address to, uint256 amount) returns (bool)',
  'function allowance(address owner, address spender) view returns (uint256)',
  'function approve(address spender, uint256 amount) returns (bool)',
  'function transferFrom(address from, address to, uint256 amount) returns (bool)',
  'function name() view returns (string)',
  'function symbol() view returns (string)',
  'function decimals() view returns (uint8)',

  // Core Configuration
  'function maxTotalAssets() view returns (uint256)',
  'function depositCapEnabled() view returns (bool)',
  'function emergencyMode() view returns (bool)',
  'function emergencyIdleFunds() view returns (uint256)',
  'function paused() view returns (bool)',
  'function depositFeeBps() view returns (uint16)',
  'function withdrawFeeBps() view returns (uint16)',
  'function feeRecipient() view returns (address)',
  'function manager() view returns (address)',
  'function getSharePrice() view returns (uint256)',
  'function getAPR() view returns (int256)',
  'function updateAPR() external',
  'function lastSharePrice() view returns (uint256)',
  'function lastPriceUpdateTime() view returns (uint256)',
  'function currentAPR() view returns (uint256)',

  // Adapter Management
  'function adapters(uint256) view returns (address)',
  'function primaryAdapterIndex() view returns (uint256)',
  'function getAdapters() view returns (address[])',
  'function adapterHealthStatus(address) view returns (uint256 lastRecordedAssets, uint256 lastCheckTime, uint256 consecutiveFailures, bool isHealthy, uint256 maxDrawdownBps)',
  'function adapterAllocations(address) view returns (uint256 targetBps, uint256 minBps, uint256 maxBps)',
  'function totalAllocationBps() view returns (uint256)',

  // Enhanced User Stats with P&L Tracking
  'function userInfo(address) view returns (uint256 totalDeposited, uint256 totalWithdrawn, uint256 totalSharesMinted, uint256 totalSharesBurned, uint256 realizedGains, uint256 realizedLosses, uint256 firstDepositTime, uint256 depositCount)',
  'function getUserStats(address user) view returns (uint256 totalDeposited, uint256 totalWithdrawn, uint256 currentValue, int256 unrealizedPnL, int256 realizedPnL, int256 totalPnL, int256 roiPercentage, uint256 sharesOwned, uint256 firstDepositTime, uint256 depositCount)',
  'function totalUsersProfit() view returns (uint256)',
  'function totalUsersLoss() view returns (uint256)',

  // Admin Functions (for reference)
  'function setFees(uint16 _depositFeeBps, uint16 _withdrawFeeBps, address _recipient) external',
  'function setDepositCap(uint256 newCap, bool enabled) external',
  'function setEmergencyMode(bool active) external',
  'function pause() external',
  'function unpause() external',
  'function addAdapter(address adapter) external',
  'function removeAdapter(uint256 index) external',
  'function setPrimaryAdapter(uint256 index) external',
  'function setManager(address _manager) external',
  'function setAdapterHealthStatus(address adapter, bool healthy) external',
  'function setAllocation(address adapter, uint256 targetBps, uint256 minBps, uint256 maxBps) external',

  // Adapter Metrics Functions
  'function getAdapterMetrics() view returns (address[] adapterAddresses, uint256[] currentBalances, int256[] apy1h, int256[] apy24h, int256[] apy7d, int256[] apy30d)',
  'function getAdapterDetailedMetrics(address adapter) view returns (uint256 totalDeposits, uint256 totalWithdrawals, uint256 netFlow, uint256 organicYield, int256 yieldPercentage, uint256 currentAssets, uint256 drawdownBps, bool supportsMetrics)',
  'function getBestPerformingAdapter(uint256 periodHours) view returns (address bestAdapter, int256 bestAPY, uint256 adapterBalance)',
  'function updateAllAdapterMetrics() external',

  // Events
  'event Deposit(address indexed sender, address indexed owner, uint256 assets, uint256 shares)',
  'event Withdraw(address indexed sender, address indexed receiver, address indexed owner, uint256 assets, uint256 shares)',
  'event SetFees(uint16 depositFeeBps, uint16 withdrawFeeBps, address feeRecipient)',
  'event AdapterAdded(uint256 indexed index, address adapter)',
  'event AdapterRemoved(uint256 indexed index, address adapter)',
]);

// VaultManager ABI - Management and performance functions
export const VAULT_MANAGER_ABI = parseAbi([
  // Performance Management
  'function performanceFeeBps() view returns (uint16)',
  'function lastSharePrice() view returns (uint256)',
  'function lastHarvestTime() view returns (uint256)',
  'function performanceFeeAccrued() view returns (uint256)',
  'function harvestPerformanceFees() external returns (uint256)',
  'function mintPerformanceFees(address recipient, uint256 shares) external',
  'function updateSharePrice() external',

  // Allocations
  'function setAdapterAllocation(address adapter, uint256 targetBps, uint256 minBps, uint256 maxBps) external',
  'function rebalanceToTargetAllocations() external',

  // Circuit Breaker
  'function circuitBreakerDropBps() view returns (uint256)',
  'function circuitBreakerTriggered() view returns (bool)',
  'function setCircuitBreaker(uint256 _maxDropBps) external',

  // Emergency
  'function emergencyWithdrawFromAdapter(address adapter, uint256 amount) external',
  'function updateEmergencyFunds(uint256 amount) external',

  // Health
  'function setAdapterHealth(address adapter, bool healthy) external',
  'function checkAdapterHealth(address adapter) external view returns (bool isHealthy, uint256 drawdown)',

  // Rewards
  'function claimAdapterRewards(address adapter) external',

  // Admin
  'function setPerformanceFee(uint16 _performanceFeeBps) external',
  'function setVaultFees(uint16 depositFeeBps, uint16 withdrawFeeBps) external',
  'function setDepositCap(uint256 newCap, bool enabled) external',
  'function pauseVault() external',
  'function unpauseVault() external',
  'function setKeeper(address keeper, bool status) external',
  'function owner() view returns (address)',
  'function vault() view returns (address)',

  // Events
  'event AllocationSet(address indexed adapter, uint256 targetBps, uint256 minBps, uint256 maxBps)',
  'event Rebalanced(uint256 totalWithdrawn, uint256 totalDeposited, uint256 adaptersRebalanced)',
  'event PerformanceFeesHarvested(uint256 feeShares, address recipient)',
  'event SharePriceUpdated(uint256 newPrice)',
  'event CircuitBreakerTriggered(uint256 currentPrice, uint256 expectedPrice, uint256 dropBps)',
  'event EmergencyWithdrawalExecuted(address adapter, uint256 withdrawn, uint256 newEmergencyFunds)',
]);

// USDC ABI (ERC20)
export const USDC_ABI = parseAbi([
  'function totalSupply() view returns (uint256)',
  'function balanceOf(address account) view returns (uint256)',
  'function transfer(address to, uint256 amount) returns (bool)',
  'function allowance(address owner, address spender) view returns (uint256)',
  'function approve(address spender, uint256 amount) returns (bool)',
  'function transferFrom(address from, address to, uint256 amount) returns (bool)',
  'function name() view returns (string)',
  'function symbol() view returns (string)',
  'function decimals() view returns (uint8)',
]);

// Adapter ABI - Common interface for all yield adapters
export const ADAPTER_ABI = parseAbi([
  'function vault() view returns (address)',
  'function getAsset() view returns (address)',
  'function totalAssets() view returns (uint256)',
  'function deposit(uint256 assets) external',
  'function withdraw(uint256 assets, address receiver) external returns (uint256)',
  'function withdrawAll(address receiver) external returns (uint256)',
  'function claimRewards(address receiver) external returns (uint256)',
]);

// Contract configurations for wagmi
export const vaultConfig = {
  vaultCore: {
    address: CONTRACTS.VAULT_CORE,
    abi: VAULT_CORE_ABI,
  },
  vaultManager: {
    address: CONTRACTS.VAULT_MANAGER,
    abi: VAULT_MANAGER_ABI,
  },
  usdc: {
    address: CONTRACTS.USDC,
    abi: USDC_ABI,
  },
  adapters: {
    aave: {
      address: CONTRACTS.AAVE_ADAPTER,
      abi: ADAPTER_ABI,
      name: 'Aave V3',
      allocation: 25, // percentage
    },
    euler: {
      address: CONTRACTS.EULER_ADAPTER,
      abi: ADAPTER_ABI,
      name: 'Euler V2',
      allocation: 25,
    },
    fortyAcres: {
      address: CONTRACTS.FORTY_ACRES_ADAPTER,
      abi: ADAPTER_ABI,
      name: '40 Acres',
      allocation: 25,
    },
    silo: {
      address: CONTRACTS.SILO_ADAPTER,
      abi: ADAPTER_ABI,
      name: 'Silo',
      allocation: 25,
    },
  },
};

// Helper functions
export const formatUSDC = (value) => {
  if (!value) return '0.00';
  const formatted = formatUnits(value, 6);
  return parseFloat(formatted).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const formatShares = (value) => {
  if (!value) return '0.0000';
  const formatted = formatUnits(value, 6); // Note: VaultCore uses 6 decimals for shares
  return parseFloat(formatted).toLocaleString(undefined, {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
};

export const formatSharePrice = (value) => {
  if (!value) return '1.000000';
  const formatted = formatUnits(value, 12); // Share price uses 1e12 precision
  return parseFloat(formatted).toLocaleString(undefined, {
    minimumFractionDigits: 6,
    maximumFractionDigits: 6,
  });
};

export const formatPercent = (value) => {
  if (!value) return '0.00%';
  return `${value.toFixed(2)}%`;
};

export const formatPnL = (value, decimals = 6) => {
  if (!value) return '$0.00';
  const formatted = formatUnits(value, decimals);
  const number = parseFloat(formatted);
  const prefix = number >= 0 ? '+' : '';
  return `${prefix}$${Math.abs(number).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const formatROI = (roiPercentage) => {
  if (!roiPercentage) return '0.00%';
  // ROI is returned as basis points (1/100 of a percent)
  const percentage = Number(roiPercentage) / 100;
  const prefix = percentage >= 0 ? '+' : '';
  return `${prefix}${percentage.toFixed(2)}%`;
};

export const formatAPY = (apy) => {
  if (!apy) return '0.00%';
  // APY is returned as basis points
  const percentage = Number(apy) / 100;
  return `${percentage.toFixed(2)}%`;
};

// Health status helpers
export const getHealthStatusColor = (isHealthy) => {
  return isHealthy ? '#4caf50' : '#f44336';
};

export const getHealthStatusText = (isHealthy) => {
  return isHealthy ? 'Healthy' : 'Unhealthy';
};

// Allocation status helpers
export const getAllocationStatus = (currentBps, targetBps, tolerance = 100) => {
  const deviation = Math.abs(currentBps - targetBps);
  if (deviation <= tolerance) return 'balanced';
  if (currentBps < targetBps) return 'underweight';
  return 'overweight';
};

export const getAllocationColor = (status) => {
  switch (status) {
    case 'balanced':
      return '#4caf50';
    case 'underweight':
      return '#ff9800';
    case 'overweight':
      return '#f44336';
    default:
      return '#9e9e9e';
  }
};
